/**
 * CLUB-676: where a row menu opens.
 *
 * A menu anchored below its trigger falls off the bottom of the window for
 * every row in the lower half of a list — and the pin collection is a long
 * list whose last row sits at the very bottom of the card. So the side is
 * chosen from the space actually available, not fixed.
 *
 * Pure, and separated from the component, because the interesting part is the
 * arithmetic: which side, and what happens when neither side has room.
 */

export type MenuPlacement = {
  /** Viewport coordinates — the menu renders `position: fixed`. */
  top: number;
  left: number;
  maxHeight: number;
  side: 'above' | 'below';
};

/** Distance kept between the menu and its trigger. */
const GAP = 4;
/** Distance kept between the menu and the window edge. */
const MARGIN = 8;

/**
 * Estimated height of a menu, from its shape rather than a measure pass.
 *
 * Rows are a fixed 40px and dividers a fixed 13px (1px rule + 6px either
 * side), so this is exact — and knowing it up front means the menu is never
 * painted in the wrong place first and corrected afterwards.
 */
export function menuHeight(itemCount: number, dividerCount: number): number {
  const VERTICAL_PADDING = 12;
  return itemCount * 40 + dividerCount * 13 + VERTICAL_PADDING;
}

export function resolveMenuPlacement(
  trigger: { top: number; bottom: number; left: number; right: number },
  size: { width: number; height: number },
  viewport: { width: number; height: number },
  align: 'left' | 'right' = 'right',
): MenuPlacement {
  const roomBelow = viewport.height - trigger.bottom - GAP - MARGIN;
  const roomAbove = trigger.top - GAP - MARGIN;

  // Below unless it does not fit AND above is roomier. "Roomier", not merely
  // "fits": when neither side fits, the menu goes where more of it will be
  // visible rather than where it happens to be tested first.
  const side: 'above' | 'below' =
    size.height <= roomBelow || roomBelow >= roomAbove ? 'below' : 'above';

  const maxHeight = Math.max(120, side === 'below' ? roomBelow : roomAbove);
  const height = Math.min(size.height, maxHeight);
  const top = side === 'below' ? trigger.bottom + GAP : trigger.top - GAP - height;

  const rawLeft = align === 'right' ? trigger.right - size.width : trigger.left;
  const left = Math.max(MARGIN, Math.min(rawLeft, viewport.width - size.width - MARGIN));

  return { top: Math.max(MARGIN, top), left, maxHeight, side };
}
